import { cloneDeep } from 'lodash';
import { useEffect, useRef } from 'react';

import { isProgramOrGuide } from '@/components/app/cards/PacksList/types';
import { usePacksContext } from '@/components/app/layout/PacksProvider';
import { AccountScreenConfig } from '@/components/SimplifiedSignup/Account/types';
import LockedContentFlow from '@/components/SimplifiedSignup/configs/LockedContent/Flow';
import { useSimplifiedSignupContext } from '@/components/SimplifiedSignup/Context';
import { HandoffScreenConfig } from '@/components/SimplifiedSignup/Handoff/types';
import { PaymentScreenConfig } from '@/components/SimplifiedSignup/Payment/types';
import { useSimplifiedSignupModalContext } from '@/context/SimplifiedSignupModalContextProvider';

import { useUserState } from '../store';

interface UseLockedContentUpsellOptions {
	// Runs when the handoff screen's CTA is pressed. Read through a ref, so a new function each
	// render does not re-open the modal.
	onHandoff?: () => Promise<void>;
}

export default function useLockedContentUpsell({ onHandoff }: UseLockedContentUpsellOptions = {}) {
	const onHandoffRef = useRef(onHandoff);
	useEffect(() => {
		onHandoffRef.current = onHandoff;
	});
	const { isModalOpen, setIsModalOpen } = useSimplifiedSignupModalContext();
	const { flowConfig, setFlowConfig } = useSimplifiedSignupContext();
	const user = useUserState();
	const { currentPackItem } = usePacksContext();

	useEffect(() => {
		if (user?.subscription?.valid) {
			return;
		}
		const _flowConfig = cloneDeep(LockedContentFlow);
		if (currentPackItem?.image?.url) {
			const subtitle = isProgramOrGuide(currentPackItem) ? currentPackItem.narrator_name : undefined;
			const contentType = isProgramOrGuide(currentPackItem) ? currentPackItem.content_type : undefined;
			const contentBasedImage = {
				src: currentPackItem?.image.url,
				isThumbnail: true,
				title: currentPackItem.title,
				subtitle,
				description: contentType,
			};
			(_flowConfig.screens.account as AccountScreenConfig).contentBasedImage = contentBasedImage;
			(_flowConfig.screens.payment as PaymentScreenConfig).contentBasedImage = contentBasedImage;
			const handoffCTA = (_flowConfig.screens.handoff as HandoffScreenConfig | undefined)?.CTA;
			if (handoffCTA) {
				handoffCTA.onClick = async () => {
					await onHandoffRef.current?.();
				};
			}
			setFlowConfig(_flowConfig);
			setIsModalOpen(true);
		}
	}, [currentPackItem, setFlowConfig, setIsModalOpen, user?.subscription?.valid]);

	return {
		isModalOpen,
		flowConfig,
	};
}
